import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import styled from "styled-components";
import { useAuth } from "../contexts/AuthContext";

// Styled Components
const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  padding: 2rem;
`;

const Spinner = styled.div`
  width: 3rem;
  height: 3rem;
  border: 2px solid #e5e7eb;
  border-top: 2px solid #2563eb;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

const LoadingText = styled.span`
  margin-left: 0.75rem;
  color: #6b7280;
  font-size: 0.875rem;
`;

const AccessDeniedContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  padding: 2rem;
  background: #f9fafb;
`;

const AccessDeniedCard = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 0.5rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  max-width: 28rem;
  width: 100%;
`;

const AccessDeniedContent = styled.div`
  text-align: center;
`;

const IconContainer = styled.div`
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 3rem;
  width: 3rem;
  border-radius: 50%;
  background: #fef2f2;
  margin-bottom: 1rem;
`;

const Icon = styled.svg`
  height: 1.5rem;
  width: 1.5rem;
  color: #dc2626;
`;

const Title = styled.h3`
  font-size: 1.125rem;
  font-weight: 500;
  color: #111827;
  margin-bottom: 0.5rem;
`;

const Description = styled.p`
  font-size: 0.875rem;
  color: #6b7280;
  margin-bottom: 1rem;
`;

const RequirementsContainer = styled.div`
  background: #f9fafb;
  padding: 1rem;
  border-radius: 0.375rem;
  text-align: left;
  margin-bottom: 1rem;
`;

const RequirementsTitle = styled.h4`
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  margin-bottom: 0.5rem;
`;

const RequirementSection = styled.div`
  margin-bottom: 0.5rem;
`;

const RequirementLabel = styled.span`
  font-size: 0.75rem;
  font-weight: 500;
  color: #6b7280;
`;

const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  margin-top: 0.25rem;
`;

const PermissionTag = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.5rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 500;
  background: #dbeafe;
  color: #1d4ed8;
`;

const RoleTag = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.5rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 500;
  background: #dcfce7;
  color: #15803d;
`;

const ButtonContainer = styled.div`
  margin-top: 1.5rem;
`;

const BackButton = styled.button`
  width: 100%;
  display: flex;
  justify-content: center;
  padding: 0.5rem 1rem;
  border: 1px solid transparent;
  border-radius: 0.375rem;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
  font-size: 0.875rem;
  font-weight: 500;
  color: white;
  background: #2563eb;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #1d4ed8;
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.5);
  }
`;

// Loading spinner component
const LoadingSpinner = () => (
  <LoadingContainer>
    <Spinner />
    <LoadingText>Loading...</LoadingText>
  </LoadingContainer>
);

// Access denied component
const AccessDenied = ({ requiredPermissions, requiredRoles }) => (
  <AccessDeniedContainer>
    <AccessDeniedCard>
      <AccessDeniedContent>
        <IconContainer>
          <Icon fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
            />
          </Icon>
        </IconContainer>
        <Title>Access Denied</Title>
        <Description>
          You don&apos;t have permission to access this page.
        </Description>

        {(requiredPermissions || requiredRoles) && (
          <RequirementsContainer>
            <RequirementsTitle>Required:</RequirementsTitle>
            {requiredPermissions && (
              <RequirementSection>
                <RequirementLabel>Permissions:</RequirementLabel>
                <TagsContainer>
                  {requiredPermissions.map((permission) => (
                    <PermissionTag key={permission}>{permission}</PermissionTag>
                  ))}
                </TagsContainer>
              </RequirementSection>
            )}
            {requiredRoles && (
              <RequirementSection>
                <RequirementLabel>Roles:</RequirementLabel>
                <TagsContainer>
                  {requiredRoles.map((role) => (
                    <RoleTag key={role}>{role}</RoleTag>
                  ))}
                </TagsContainer>
              </RequirementSection>
            )}
          </RequirementsContainer>
        )}

        <ButtonContainer>
          <BackButton onClick={() => window.history.back()}>Go Back</BackButton>
        </ButtonContainer>
      </AccessDeniedContent>
    </AccessDeniedCard>
  </AccessDeniedContainer>
);

const ProtectedRoute = ({
  children,
  requiredPermissions = [],
  requiredRoles = [],
  requireAuth = true,
  fallback = null,
}) => {
  const {
    isAuthenticated,
    isLoading,
    permissions,
    role,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasRole,
    hasAnyRole,
  } = useAuth();
  const location = useLocation();

  // Show loading spinner while checking authentication
  if (isLoading) {
    return <LoadingSpinner />;
  }

  // If authentication is not required, render children
  if (!requireAuth) {
    return children;
  }

  // If not authenticated, redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check permissions if required
  if (requiredPermissions.length > 0) {
    let hasAccess = false;

    // Check if user has any of the required permissions
    if (Array.isArray(requiredPermissions[0])) {
      // Multiple permission groups (OR logic between groups, AND logic within groups)
      hasAccess = requiredPermissions.some((group) =>
        Array.isArray(group) ? hasAllPermissions(group) : hasPermission(group)
      );
    } else {
      // Single permission or array of permissions (ANY logic)
      hasAccess = hasAnyPermission(requiredPermissions);
    }

    if (!hasAccess) {
      return <AccessDenied requiredPermissions={requiredPermissions} />;
    }
  }

  // Check roles if required
  if (requiredRoles.length > 0) {
    const hasRequiredRole = hasAnyRole(requiredRoles);
    if (!hasRequiredRole) {
      return <AccessDenied requiredRoles={requiredRoles} />;
    }
  }

  // If custom fallback is provided, render it
  if (fallback) {
    return fallback;
  }

  // All checks passed, render children
  return children;
};

// Convenience components for common permission patterns
export const RequirePermission = ({ permission, children, fallback }) => (
  <ProtectedRoute requiredPermissions={[permission]} fallback={fallback}>
    {children}
  </ProtectedRoute>
);

export const RequireAnyPermission = ({ permissions, children, fallback }) => (
  <ProtectedRoute requiredPermissions={permissions} fallback={fallback}>
    {children}
  </ProtectedRoute>
);

export const RequireAllPermissions = ({ permissions, children, fallback }) => (
  <ProtectedRoute requiredPermissions={[permissions]} fallback={fallback}>
    {children}
  </ProtectedRoute>
);

export const RequireRole = ({ role, children, fallback }) => (
  <ProtectedRoute requiredRoles={[role]} fallback={fallback}>
    {children}
  </ProtectedRoute>
);

export const RequireAnyRole = ({ roles, children, fallback }) => (
  <ProtectedRoute requiredRoles={roles} fallback={fallback}>
    {children}
  </ProtectedRoute>
);

// Role-based convenience components
export const RequireAdmin = ({ children, fallback }) => (
  <ProtectedRoute requiredRoles={["admin", "super_admin"]} fallback={fallback}>
    {children}
  </ProtectedRoute>
);

export const RequireSuperAdmin = ({ children, fallback }) => (
  <ProtectedRoute requiredRoles={["super_admin"]} fallback={fallback}>
    {children}
  </ProtectedRoute>
);

export const RequireAgent = ({ children, fallback }) => (
  <ProtectedRoute
    requiredRoles={["agent", "admin", "super_admin"]}
    fallback={fallback}
  >
    {children}
  </ProtectedRoute>
);

export default ProtectedRoute;
